import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { GalleryMedia } from '../../types';
import InfoBlock from '../../InfoBlock';
import ParametersBlock from '../../ParametersBlock';
import ReferenceImagesBlock from '../../ReferenceImagesBlock';
import { ActionButton } from '../../../Common/Buttons/ActionButton';
import {
  CloseIcon,
  PlusIcon,
  DownloadIcon,
  AnimateIcon,
  UpscaleIcon,
  EditIcon,
  RegenerateIcon,
} from '../../icons';
import styles from './ActionPanel.module.css';

interface ActionPanelProps {
  image: GalleryMedia;
  isVideo: boolean;
  isSvg: boolean;
  canRegenerate: boolean;
  isDownloading: boolean;
  confirmingDelete: boolean;
  showActionPanel: boolean;
  onRegenerate?: (e: React.MouseEvent) => void;
  onUse?: (e: React.MouseEvent) => void;
  onUpscale?: (e: React.MouseEvent) => void;
  onEditImage?: (e: React.MouseEvent) => void;
  onAnimate?: (e: React.MouseEvent) => void;
  onUseVideo?: (e: React.MouseEvent) => void;
  onDownload: (e?: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
  onUseReference?: (url: string, isVideo: boolean) => void;
  hideDownload?: boolean;
  hideDelete?: boolean;
  hideParameters?: boolean;
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
}

export const ActionPanel: React.FC<ActionPanelProps> = ({
  image,
  isVideo,
  isSvg,
  canRegenerate,
  isDownloading,
  confirmingDelete,
  showActionPanel,
  onRegenerate,
  onUse,
  onUpscale,
  onEditImage,
  onAnimate,
  onUseVideo,
  onDownload,
  onDelete,
  panelRef,
  onUseReference,
  hideDownload,
  hideDelete,
  hideParameters,
  primaryAction,
}) => {
  const { t } = useTranslation(['image', 'common']);

  return (
    <div
      ref={panelRef as React.RefObject<HTMLDivElement>}
      className={clsx(styles.inlineActionPanel, showActionPanel && styles.inlineActionPanelOpen)}
    >
      <div className={styles.inlineActionPanelScroll}>
        <div className={styles.inlineActionPanelButtons}>
          {canRegenerate && (
            <div className={styles.btnCell}>
              <ActionButton icon={<RegenerateIcon />} label={t('gallery.repeat')} onClick={onRegenerate} />
            </div>
          )}
          {!isVideo && !isSvg && onUse && (
            <div className={styles.btnCell}>
              <ActionButton icon={<PlusIcon />} label={t('gallery.useAsReference')} onClick={onUse} />
            </div>
          )}
          {!isVideo && !isSvg && onUpscale && (
            <div className={styles.btnCell}>
              <ActionButton icon={<UpscaleIcon />} label={t('gallery.upscale')} onClick={onUpscale} />
            </div>
          )}
          {!isVideo && !isSvg && onEditImage && (
            <div className={styles.btnCell}>
              <ActionButton icon={<EditIcon />} label={t('gallery.editImage')} onClick={onEditImage} />
            </div>
          )}
          {!isVideo && !isSvg && onAnimate && (
            <div className={clsx(styles.btnCell, styles.fullWidth)}>
              <ActionButton icon={<AnimateIcon />} label={t('gallery.animate')} onClick={onAnimate} />
            </div>
          )}
          {isVideo && onUseVideo && (
            <div className={clsx(styles.btnCell, styles.fullWidth)}>
              <ActionButton icon={<EditIcon />} label={t('gallery.useInVideoEdit')} onClick={onUseVideo} />
            </div>
          )}
        </div>
        {(primaryAction || !hideDownload || !hideDelete) && (
          <div className={styles.inlineFooterActions}>
            {primaryAction ? (
              <ActionButton
                icon={primaryAction.icon}
                label={primaryAction.label}
                onClick={primaryAction.onClick}
                active
                noGlow
              />
            ) : !hideDownload && (
              <ActionButton
                icon={isDownloading ? <i className="fa-solid fa-circle-notch fa-spin" /> : <DownloadIcon />}
                label={t('btn.download', { ns: 'common' })}
                onClick={onDownload}
                disabled={isDownloading}
                active
                noGlow
              />
            )}
            {!hideDelete && (
              <button className={clsx(styles.inlineDeleteBtn, confirmingDelete && styles.confirming)} onClick={onDelete}>
                <CloseIcon />
                {confirmingDelete && <span>{t('gallery.confirmDeleteInline')}</span>}
              </button>
            )}
          </div>
        )}

        <div className={styles.inlineActionDivider} />

        <div className={styles.inlineActionInfo}>
          {image.shots && image.shots.length > 0 ? (
            image.shots.map((shot, i) => (
              <InfoBlock
                key={shot.id}
                label={`${t('gallery.scene')} ${i + 1} (${shot.duration}${t('gallery.sec')})`}
                value={shot.prompt}
                canCopy
                collapsible
              />
            ))
          ) : !image.isUpscaled && (
            <InfoBlock
              label={image.presetName ? t('gallery.presetLabel') : t('gallery.prompt')}
              value={image.metadata.prompt}
              canCopy={!image.presetName}
              collapsible
            />
          )}
          {image.referenceImages && image.referenceImages.length > 0 && (
            <ReferenceImagesBlock
              images={image.referenceImages}
              labels={image.referenceImageLabels}
              isVideoFlags={image.referenceIsVideo}
              onUseReference={onUseReference}
            />
          )}
          {!hideParameters && (
            <ParametersBlock
              model={image.metadata.model}
              provider={image.provider}
              time={image.metadata.generationTime}
              format={image.metadata.format}
              quality={image.metadata.quality}
              resolution={image.metadata.resolution}
              duration={image.metadata.duration ? `${image.metadata.duration}с` : undefined}
              stylize={image.metadata.stylize}
              chaos={image.metadata.chaos}
              weird={image.metadata.weird}
              seed={image.metadata.seed}
              niji={image.metadata.niji}
              mjQuality={image.metadata.mjQuality}
            />
          )}
        </div>
      </div>
    </div>
  );
};
